import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { callCaseAnalyst, getErrorMessage } from '@/utils/ai'
import MarkdownText from '@/components/common/MarkdownText'
import { PREFILL_KEY } from '@/utils/agentHandoff'
import VoiceAgent from '@/components/VoiceAgent'

// ── Situation type options ─────────────────────────────────────────────────────
const SITUATION_TYPES = [
  { value: 'custody',    label: '👨‍👧 Custody dispute' },
  { value: 'divorcing',  label: '⚖️ Divorce proceedings' },
  { value: 'cps',        label: '🛡️ CPS investigation' },
  { value: 'coparenting',label: '🤝 Co-parenting issue' },
  { value: 'support',    label: '💰 Child/spousal support' },
  { value: 'modification',label: '📋 Modify existing order' },
]

// ── Tool links from recommended steps ────────────────────────────────────────
const TOOL_MAP = {
  chat:         { emoji: '🤖', label: 'AI Legal Chat',  to: '/chat' },
  documents:    { emoji: '📄', label: 'Documents',       to: '/documents' },
  'cps-tracker':{ emoji: '🛡️', label: 'CPS Tracker',    to: '/cps' },
  'comms-log':  { emoji: '💬', label: 'Comms Log',       to: '/comms' },
  'event-log':  { emoji: '📋', label: 'Event Log',       to: '/events' },
  attorney:     { emoji: '👩‍⚖️', label: 'Consult Attorney', to: null },
}

// ── Urgency / attorney badge styles ───────────────────────────────────────────
const URGENCY_STYLES = {
  immediate: 'bg-red-100 text-red-700 border-red-200',
  high:      'bg-orange-100 text-orange-700 border-orange-200',
  medium:    'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:       'bg-green-100 text-green-700 border-green-200',
}
const ATTORNEY_STYLES = {
  immediate:   { style: 'bg-red-600 text-white',     label: '👩‍⚖️ Attorney Urgency: IMMEDIATE' },
  recommended: { style: 'bg-amber-500 text-white',   label: '👩‍⚖️ Attorney Consultation Recommended' },
  optional:    { style: 'bg-slate-200 text-slate-700', label: '👩‍⚖️ Attorney Optional' },
}

// ── Section card ──────────────────────────────────────────────────────────────
function Section({ emoji, title, children }) {
  return (
    <div className="card flex flex-col gap-3">
      <h3 className="font-bold text-slate-800 text-base flex items-center gap-2">
        <span>{emoji}</span> {title}
      </h3>
      {children}
    </div>
  )
}

// ── Assessment results view ────────────────────────────────────────────────────
function AssessmentView({ assessment, onReset }) {
  // Handle rare parse error or emergency response
  if (assessment.emergency) {
    return (
      <div className="flex flex-col gap-4">
        <div className="card bg-red-50 border border-red-300 flex flex-col gap-3 items-center text-center py-8">
          <span className="text-5xl">🚨</span>
          <p className="font-bold text-red-700 text-lg">Emergency Detected</p>
          <p className="text-sm text-red-600">{assessment.message}</p>
        </div>
        <Button variant="secondary" size="md" fullWidth onClick={onReset}>Start Over</Button>
      </div>
    )
  }

  if (assessment.parse_error) {
    return (
      <div className="flex flex-col gap-4">
        <div className="card bg-amber-50 border border-amber-200">
          <p className="text-sm font-semibold text-amber-800 mb-2">AI Response (unstructured)</p>
          <div className="text-sm text-slate-700">
            <MarkdownText content={assessment.raw_response} hideAttorneyButton />
          </div>
        </div>
        <Button variant="secondary" size="md" fullWidth onClick={onReset}>New Assessment</Button>
      </div>
    )
  }

  const urgencyStyle  = URGENCY_STYLES[assessment.urgency]  || URGENCY_STYLES.medium
  const attorneyBadge = ATTORNEY_STYLES[assessment.attorney_urgency] || ATTORNEY_STYLES.recommended

  return (
    <div className="flex flex-col gap-4 pb-4">

      {/* Top summary row */}
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-brand-100 text-brand-700 capitalize">
          📂 {assessment.domain}
        </span>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize ${urgencyStyle}`}>
          🔥 Urgency: {assessment.urgency}
        </span>
        {assessment.confidence && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-600">
            🎯 Confidence: {Math.round(assessment.confidence * 100)}%
          </span>
        )}
      </div>

      {/* Attorney urgency banner */}
      <div className={`rounded-xl px-4 py-3 text-sm font-semibold ${attorneyBadge.style}`}>
        {attorneyBadge.label}
      </div>

      {/* Jurisdiction notes */}
      {assessment.jurisdiction_notes && (
        <Section emoji="📍" title="Jurisdiction Notes">
          <p className="text-sm text-slate-700 leading-relaxed">{assessment.jurisdiction_notes}</p>
        </Section>
      )}

      {/* Key issues */}
      {assessment.key_issues?.length > 0 && (
        <Section emoji="⚖️" title="Key Legal Issues">
          <div className="flex flex-col gap-3">
            {assessment.key_issues.map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-3 flex flex-col gap-1">
                <p className="text-sm font-bold text-slate-800">{item.issue}</p>
                {item.applicable_law && (
                  <p className="text-sm text-brand-700 font-medium">📖 {item.applicable_law}</p>
                )}
                {item.education && (
                  <p className="text-sm text-slate-600 leading-relaxed">{item.education}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Strengths & Challenges */}
      <div className="grid grid-cols-1 gap-3">
        {assessment.strengths?.length > 0 && (
          <Section emoji="✅" title="Potential Strengths">
            <ul className="flex flex-col gap-1.5">
              {assessment.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-base text-slate-700">
                  <span className="text-green-600 flex-shrink-0 mt-0.5">✓</span> {s}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {assessment.challenges?.length > 0 && (
          <Section emoji="⚠️" title="Potential Challenges">
            <ul className="flex flex-col gap-1.5">
              {assessment.challenges.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-base text-slate-700">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">!</span> {c}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {/* Recommended steps */}
      {assessment.recommended_steps?.length > 0 && (
        <Section emoji="🗺️" title="Recommended Next Steps">
          <div className="flex flex-col gap-3">
            {assessment.recommended_steps.map((step) => {
              const tool = TOOL_MAP[step.tool]
              return (
                <div key={step.step} className="flex gap-3 items-start">
                  <div className="w-6 h-6 rounded-full bg-brand-700 text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                    {step.step}
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <p className="text-base font-semibold text-slate-800">{step.action}</p>
                    {step.rationale && (
                      <p className="text-sm text-slate-500 leading-relaxed">{step.rationale}</p>
                    )}
                    {tool && tool.to && (
                      <a href={tool.to}
                        className="self-start text-sm font-medium text-brand-700 bg-brand-50 px-2 py-1.5 rounded-lg hover:bg-brand-100 transition-colors mt-1">
                        {tool.emoji} Open {tool.label} →
                      </a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Section>
      )}

      {/* Documents needed */}
      {assessment.documents_needed?.length > 0 && (
        <Section emoji="📄" title="Documents to Gather / Prepare">
          <ul className="flex flex-col gap-1.5">
            {assessment.documents_needed.map((d, i) => (
              <li key={i} className="text-base text-slate-700 flex items-start gap-2">
                <span className="text-brand-500 flex-shrink-0">▸</span> {d}
              </li>
            ))}
          </ul>
          <a href="/documents"
            className="self-start text-sm font-medium text-brand-700 bg-brand-50 px-3 py-1.5 rounded-lg hover:bg-brand-100 transition-colors">
            📄 Open Document Generator →
          </a>
        </Section>
      )}

      {/* Disclaimer */}
      {assessment.disclaimer && (
        <div className="disclaimer">{assessment.disclaimer}</div>
      )}

      <Button variant="secondary" size="md" fullWidth onClick={onReset}>
        New Assessment
      </Button>
    </div>
  )
}

// ── Case Analyst screen ────────────────────────────────────────────────────────
export default function CaseAnalyst() {
  const { user } = useApp()
  const location = useLocation()
  const [form, setForm]           = useState({
    situation:      '',
    state:          user?.state || '',
    situation_type: user?.situation || '',
  })
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)
  const [assessment, setAssessment] = useState(null)

  // Pre-fill situation from agent handoff
  useEffect(() => {
    const prefill = location.state?.[PREFILL_KEY]
    if (prefill) {
      setForm(p => ({ ...p, situation: prefill }))
      window.history.replaceState({}, '')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleAnalyze = async () => {
    if (!form.situation.trim()) return
    setLoading(true)
    setError(null)
    try {
      const result = await callCaseAnalyst(form)
      setAssessment(result.assessment)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Case Analyst" showBack />

      <div className="screen no-scrollbar px-4 py-5 flex flex-col gap-4">

        {!assessment ? (
          <>
            {/* Intro */}
            <div className="card bg-brand-50 border border-brand-200">
              <p className="font-bold text-slate-800 text-base mb-1">🔍 AI Case Assessment</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Describe your situation and the AI will analyze the key legal issues,
                identify potential strengths and challenges, and recommend your next steps.
              </p>
            </div>

            {/* Situation type */}
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">
                What best describes your situation?
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SITUATION_TYPES.map(st => (
                  <button key={st.value} onClick={() => set('situation_type', st.value)}
                    className={[
                      'text-sm px-3 py-2.5 rounded-xl font-medium border-2 text-left transition-all',
                      form.situation_type === st.value
                        ? 'bg-brand-100 text-brand-700 border-brand-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                    ].join(' ')}>
                    {st.label}
                  </button>
                ))}
              </div>
            </div>

            {/* State */}
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">
                Your state / jurisdiction
              </label>
              <input
                type="text"
                value={form.state}
                onChange={e => set('state', e.target.value)}
                placeholder="e.g. Florida, California, Texas…"
                className="input-base text-base"
              />
            </div>

            {/* Situation description */}
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">
                Describe your situation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.situation}
                onChange={e => set('situation', e.target.value)}
                placeholder="Example: My ex-spouse filed for full custody last month. We have a 6-year-old. Our current order gives me 50/50 parenting time. The reason given is a change in my work schedule. We are in Miami-Dade County, Florida…"
                rows={6}
                className="input-base resize-none text-base"
              />
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-slate-500 flex-1">
                  The more detail you provide, the more accurate your assessment will be.
                </p>
                <VoiceAgent
                  agent="case-analyst"
                  onTranscript={(text, role) => {
                    if (role === 'user' && text) {
                      set('situation', (form.situation ? form.situation + ' ' : '') + text)
                    }
                  }}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="disclaimer">
              ⚠️ This assessment is legal education, not legal advice. Always consult a
              licensed family law attorney before making legal decisions.
            </div>

            <Button
              variant="primary" size="lg" fullWidth
              loading={loading}
              disabled={!form.situation.trim()}
              onClick={handleAnalyze}
            >
              {loading ? 'Analyzing your case…' : '🔍 Analyze My Case'}
            </Button>
          </>
        ) : (
          <AssessmentView
            assessment={assessment}
            onReset={() => { setAssessment(null); setError(null) }}
          />
        )}
      </div>
    </div>
  )
}
